// EVENT TRACKING IMPLEMENTATION GUIDE
// --------------------------------
// To track events in this file, follow these steps:
//
// 1. Import the useEventTracking hook:
//    import { useEventTracking } from './hooks/useEventTracking';
//
// 2. Use the hook in your component:
//    const tracking = useEventTracking();
//
// 3. Track page views (automatic when component mounts):
//    useEffect(() => {
//      if (info && info.event_id) {
//        tracking.trackEventView(info);
//      }
//    }, [info]);
//
// 4. Track registrations:
//    // For free events
//    tracking.trackEventRegistration(info, 'standard', 0);
//
//    // For paid events
//    tracking.trackEventRegistration(info, 'vip', 25.99);
//
// 5. Track sharing:
//    tracking.trackEventShare(info, 'facebook');
//
// For full implementation, refer to the analytics documentation.

import React, { useState, useEffect } from "react";
import { useAppData } from "./context/DataContext";
import { useAnalytics } from "./context/AnalyticsContext";
import { getDateIndex } from "./information.service.js";
import { useLocation } from "react-router-dom";
import { ReactComponent as Location } from "./Location.svg";
import { useParams, Link } from "react-router-dom";
import { slugifyOrgName } from "./utils/slug";
import Navbar from "./Navbar";
import Footer from "./Footer";
import {
  ClockCircleOutlined,
  CalendarOutlined,
  LaptopOutlined,
} from "@ant-design/icons";
import { TicketIcon } from "lucide-react";
import { EditOutlined } from '@ant-design/icons';
import OrganizerNavbar from "./OrganizerNavbar.js";
import axios from "axios";
import ShareButton from "./ShareButton.js";
import RegisterModal from "./RegisterModal.js";
import RequestRegistrationModal from "./RequestRegistrationModal.js";
import EditRSVP from "./EditRSVP.js";
import { Helmet } from "react-helmet";
import Signin from "./Signin.js";
import OrganizerSignIn from "./OrganizerSignIn.js";
import OrgEditEvent from "./OrgEditEvent.js";
import AttendeesModal from "./AttendeesModal";
import CustomFormModal from "./CustomFormModal.js";
import TicketScanner from "./TicketScanner.js";
import { Modal, ConfigProvider, Tooltip, Form, Button } from "antd";
import { Users, MapPin } from "lucide-react";
import { getPriceInFormat } from "./helpers/price.js";
import PricingTierModal from "./PricingTierModal";
import AddToCalendar from "./AddToCalendar.js";
import AddToWalletButton from "./AddToWalletButton.js";
import { getUserEventTickets } from "./tickets.service";

// Add these styles
const ticketsContainerStyle = {
  maxHeight: "400px",
  overflowY: "auto",
  padding: "10px",
};

const ticketItemStyle = {
  marginBottom: "20px",
  textAlign: "center",
};

const ticketContentStyle = {
  width: "60%",
  height: "210px",
  backgroundColor: "#fff",
  borderRadius: "8px",
  margin: "0 auto",
  overflow: "hidden",
};

const ticketImageStyle = {
  borderRadius: "8px",
  width: "100%",
  height: "100%",
  objectFit: "contain",
};

const ticketUsedStyle = {
  textAlign: "center",
  color: "green",
  marginTop: "5px",
  fontWeight: "bold",
};

const ticketLoadingStyle = {
  textAlign: "center",
  margin: "30px auto",
  color: "#666",
};

const EventShell = () => {


  const {
    information,
    informationWithPastEvents,
    loggedInUser,
    loggedInEmail,
    userInfo,
    selectedOrganization,
    users,
  } = useAppData();

  const analytics = useAnalytics();

  const [scannerFromQR, setScannerFromQR] = useState(false);
  const [qrUserId, setQrUserId] = useState(null);
  const [isTickerScannerOpen, setIsTicketScannerOpen] = useState(false);

  const handleOpenTicketScanner = () => {
    setIsTicketScannerOpen(true);
  };

  const handleCloseTicketScanner = () => {
    setIsTicketScannerOpen(false);
  };

  useEffect(() => {
    console.log("event data:");
    console.log(information);
  }, [information]);

  const secrets = require("./secrets.js");
  const api = secrets.server_url;

  let { id } = useParams();
  // id = Number(id)

  const location = useLocation();
  
  let info;
  if (!isNaN(id) && !isNaN(parseFloat(id))) {
    info = informationWithPastEvents?.find((item) => item.event_id == id);
  } else {
    info = informationWithPastEvents?.find((item) => item.customUrl == id);
  }

  // Initialize info with safe defaults if it doesn't exist (to prevent crashes during hook initialization)
  const safeInfo = info || {
    registered: [[]],
    approvedAttendees: [[]],
    checkedInAttendees: [[]],
    dates: [],
    dateIndex: 0,
    frequency: "Once"
  };

  // Initialize registered array if it doesn't exist or is empty
  if (!safeInfo.registered || !Array.isArray(safeInfo.registered)) {
    safeInfo.registered = [[]];
  } else if (safeInfo.registered.length === 0) {
    safeInfo.registered.push([]);
  }

  // Initialize approvedAttendees if it doesn't exist
  if (!safeInfo.approvedAttendees || !Array.isArray(safeInfo.approvedAttendees)) {
    safeInfo.approvedAttendees = [[]];
  }

  // Initialize checkedInAttendees if it doesn't exist
  if (!safeInfo.checkedInAttendees || !Array.isArray(safeInfo.checkedInAttendees)) {
    safeInfo.checkedInAttendees = [[]];
  }

  // const dateIndex = findFirstFutureDate(safeInfo.dates)
  const dateIndex = safeInfo.dateIndex ?? 0;

  // Safely get startDate
  let startDate = null;
  if (safeInfo.dates && Array.isArray(safeInfo.dates) && safeInfo.dates.length > 0) {
    const dateToUse = safeInfo.frequency === "Once" ? safeInfo.dates[0] : (safeInfo.dates[dateIndex] || safeInfo.dates[0]);
    if (dateToUse && typeof dateToUse === 'string') {
      startDate = dateToUse.split("T")[0];
    }
  }

  // Safely get endDate
  let endDate = null;
  if (safeInfo.dates && Array.isArray(safeInfo.dates) && safeInfo.dates.length > 0) {
    const dateToUse = safeInfo.frequency === "Once"
      ? safeInfo.dates[safeInfo.dates.length - 1]
      : (safeInfo.dates[dateIndex] || safeInfo.dates[0]);
    if (dateToUse && typeof dateToUse === 'string') {
      endDate = dateToUse.split("T")[0];
    }
  }

  const registeredIndex = safeInfo.frequency === "Once" ? 0 : (dateIndex ?? 0);
  
  // Safely get startTime
  const startTime = safeInfo.start_time || (safeInfo.start_datetime && typeof safeInfo.start_datetime === 'string' 
    ? safeInfo.start_datetime.split("T")[1] 
    : null);
  
  // Safely get endTime
  const endTime = safeInfo.end_time || (safeInfo.end_datetime && typeof safeInfo.end_datetime === 'string'
    ? safeInfo.end_datetime.split("T")[1]
    : null);

  //console.log(safeInfo)

  console.log(loggedInEmail);
  console.log(dateIndex);

  if (safeInfo.registered && safeInfo.registered[0]) {
    console.log(safeInfo.registered[0]);
  }

  console.log(safeInfo.registered);
  console.log(registeredIndex);
  const [isAdmin, setIsAdmin] = useState(userInfo ? userInfo.isAdmin : false);
  const [registerModal, setRegisterModal] = useState(false);
  const [requestRegistrationModal, setRequestRegistrationModal] =
    useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

  const [isRegistered, setIsRegistered] = useState(
    loggedInEmail?.data && safeInfo.registered?.[registeredIndex]?.includes(loggedInEmail.data) || false
  );
  const [isApproved, setIsApproved] = useState(
    safeInfo.requireApproval &&
    isRegistered &&
    loggedInEmail?.data &&
    safeInfo.approvedAttendees?.[registeredIndex]?.includes(loggedInEmail.data) || false
  );
  const [editRsvp, setEditRsvp] = useState(false);
  const [adminEditEvent, setAdminEditEvent] = useState(false);
  const [orgEditEvent, setOrgEditEvent] = useState(false);

  const [user, setUser] = useState(loggedInUser?.data || null);
  const [email, setEmail] = useState(loggedInEmail?.data || null);

  const [buttonSignin, setButtonSignin] = useState(false);
  const [organizerSignin, setOrganizerSignin] = useState(false);

  const [isPaid, setIsPaid] = useState(false);

  const [hostOrg, setHostOrg] = useState(null);

  // Add this new state for user tickets
  const [userEventTickets, setUserEventTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // Add this new function to fetch user tickets for the event
  const fetchUserTickets = async () => {
    if (userInfo && userInfo.email && safeInfo && safeInfo.event_id) {
      try {
        setTicketsLoading(true);
        const tickets = await getUserEventTickets(
          userInfo.email,
          safeInfo.event_id
        );
        setUserEventTickets(tickets);
        setTicketsLoading(false);
      } catch (error) {
        console.error("Error fetching user tickets:", error);
        setTicketsLoading(false);
      }
    }
  };

  // Call this function when needed
  useEffect(() => {
    if (userInfo && userInfo.email && safeInfo && safeInfo.event_id) {
      fetchUserTickets();
    }
  }, [userInfo, safeInfo]);

  // const verifyPayment = async () => {
  //   try {
  //     const { data } = await axios.get(`${api}stripe/retrieve-session/${sessionId}`, {
  //       params: {
  //         organizationId: organizationId // Add the org ID
  //       }
  //     }
  //     );

  //     console.log(data)
  //     if (data.session.payment_status === 'paid') {
  //       ///add to registered and send conf email
  //       setIsPaid(true)
  //       console.log("paid for event")

  //       await handleRegister(id)

  //       // window.location.href = window.location.href.split('?')[0];

  //     }
  //   } catch (error) {
  //     console.error('Verification error:', error);
  //   }
  // };

  // if (!isRegistered && sessionId) {
  //   verifyPayment();
  // }

  async function getHostOrg() {
    try {
      const response = await axios.get(`${api}organizations/`, {
        headers: {
          "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
        },
        params: {
          name: safeInfo.organization,
        },
      });
      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data[0]
      ) {
        return response.data.data[0];
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  useEffect(() => {
    async function fetchHostOrg() {
      try {
        const orgData = await getHostOrg();
        setHostOrg(orgData);
      } catch (error) {
        console.error("Error fetching host organization:", error);
      }
    }

    fetchHostOrg();
  }, [safeInfo.organization]); // Add safeInfo.organization as a dependency

  // for ticket qr code scanning
  useEffect(() => {
    // Get URL parameters
    const queryParams = new URLSearchParams(location.search);
    const userId = queryParams.get("userId");

    if (userId) {
      setQrUserId(userId);

      // If user is logged in and is an organizer, open scanner
      if (user && (isAdmin || selectedOrganization === safeInfo.organization)) {
        setScannerFromQR(true);
        handleOpenTicketScanner();

        // Clean up URL parameters
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
      // If user is not logged in, redirect to organizer sign in
      else if (!user) {
        setOrganizerSignin(true);
        // URL parameters will persist through redirect and sign in
      }
      // If user is logged in but not an organizer, show message and clean up URL
      else {
        Modal.info({
          title: "Access Denied",
          content: "Only event organizers can scan tickets.",
        });
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [user, isAdmin, selectedOrganization, location.search]);

  const signInCallback = () => {
    // After successful sign in, check if there's a pending QR scan
    if (qrUserId && (isAdmin || selectedOrganization === safeInfo.organization)) {
      setScannerFromQR(true);
      setIsTicketModalVisible(true);
    }
  };

  const formattedDescription = safeInfo.description?.replace(/\\n/g, "\n") || '';

  window.scrollTo(0, 0);

  function GoogleMap({ address }) {
    const apiKey = secrets.google_maps_api_key;

    return (
      <>
        {address ? (
          <a
            className="es-map"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              address
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <iframe
              title="Google Map"
              className="event-shell-map"
              frameBorder="0" // Remove the default border
              allowFullScreen
              loading="eager"
              src={`https://www.google.com/maps/embed/v1/place?q=${encodeURIComponent(
                address
              )}&zoom=15&key=${apiKey}`}
            ></iframe>
          </a>
        ) : (
          <div class="map-error-mask"></div>
        )}
      </>
    );
  }

  async function getUserDetails(email) {
    try {
      const response = await axios.get(`${api}users/${email}/`, {
        headers: {
          "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
        },
      });
      //console.log("User details:", response.data.data[0].email);
      return response.data.data;
    } catch (error) {
      console.error(error);
      throw error; // Handle or rethrow error
    }
  }

  const updateUserDetails = async (email, updates) => {
    try {
      const response = await axios.put(`${api}users/${email}/`, updates, {
        headers: {
          "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
        },
      });
      return response.data.data;
    } catch (error) {
      console.error("Error updating user details:", error);
      throw error;
    }
  };

  const uploadTicketImg = async (base64Image, fileName) => {
    try {
      const response = await axios.post(`${api}uploadBase64Image/`, {
        base64Image,
        fileName,
      });
      console.log(
        "Base64 image uploaded successfully:",
        response.data.imageUrl
      );
      return response.data.imageUrl;
    } catch (error) {
      console.error("Error uploading base64 image:", error);
    }
  };

  // Track page view once event info is loaded
  useEffect(() => {
    if (info && info.event_id) {
      analytics.trackEventView(info.event_id, info.name, {
        organizationId: info.organization_id,
        organizationName: info.organization,
        isPaid: info.price > 0,
        eventDate:
          info.frequency === "Once" ? info.dates[0] : info.dates[dateIndex],
        eventTags: info.tags,
        source: location.state?.source || "direct",
        referrer: document.referrer,
      });
    }
  }, [info?.event_id]);

  // Enhanced sharing function with tracking
  const handleShareEvent = (method) => {
    if (info && info.event_id) {
      analytics.trackEventShare(info.event_id, info.name, method, {
        organizationId: info.organization_id,
        organizationName: info.organization,
      });
    }
    // Original share functionality would go here
  };

  // Register function with tracking
  async function handleRegister(id) {
    if (!user) {
      alert("you need to sign in");
      return;
    }

    if (!id) {
      return;
    }

    var currentEventData = await axios.get(`${api}events/${id}`, {
      headers: {
        "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
      },
    });

    if (!currentEventData?.data?.data) {
      console.error('Invalid API response structure');
      return;
    }

    currentEventData = currentEventData.data.data;
    const currRegisteredIndex = info.frequency === "Once" ? 0 : dateIndex;

    if (!currentEventData) {
      return;
    }

    if (currentEventData.registered?.[currRegisteredIndex]?.includes(email)) {
      return;
    }

    if (currentEventData.registered?.[currRegisteredIndex]) {
      currentEventData.registered[currRegisteredIndex].push(email);
    }

    try {
      const response = await axios.put(
        `${api}events/${info.event_id}`,
        currentEventData,
        {
          withCredentials: true,
          headers: {
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        }
      );

      const userDetails = await getUserDetails(email);
      userDetails.signedUpEvents.push(info.event_id);

      const updatedUserDetails = await updateUserDetails(email, {
        signedUpEvents: userDetails.signedUpEvents,
      });

      //generate ticket
      let ticketURL;

      const ticketResponse = await axios.post(
        `${api}generateTicket`,
        {
          eventId: info.event_id,
          userId: userInfo._id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        }
      );

      ticketURL = await uploadTicketImg(
        ticketResponse.data.data,
        info.event_id + "-" + (loggedInEmail?.data || '')
      );

      // Create ticket in the new Tickets collection
      try {
        await axios.post(
          `${api}createFreeTicket`,
          {
            eventId: info.event_id,
            userId: userInfo._id,
            email: userInfo.email,
            qrCodeUrl: ticketURL,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
            },
          }
        );

        // Refresh the user's tickets for this event
        await fetchUserTickets();
      } catch (error) {
        console.error("Error creating ticket in new system:", error);
      }

      // For backward compatibility, still update the user's tickets object
      // This can be removed once the migration is complete
      let newTickets = userInfo.tickets || {};
      newTickets[info.event_id] = ticketURL;

      await axios.put(
        `${api}users/${loggedInEmail.data}`,
        {
          tickets: newTickets,
        },
        {
          withCredentials: true,
          headers: {
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        }
      );

      //send confirmation email
      if (loggedInEmail.data) {
        const payload = {
          email: loggedInEmail.data,
          eventName: info.name,
          eventURL: window.location.href,
          ticket: ticketURL, //ticketURL
          type: "Register for Event",
          eventLocation: info.location,
          dateTime:
            new Date(startDate + "T00:00:00").toLocaleDateString("en-US") +
            " @ " +
            new Date(`2000-01-01T${startTime}`).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }),
        };

        try {
          const response = await axios.post(`${api}sendEmail`, payload, {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
            },
          });
          console.log("Response:", response.data);
        } catch (error) {
          console.error(
            "Error:",
            error.response ? error.response.data : error.message
          );
        }
      }
    } catch (error) {
      console.error("Error updating event or user details:", error);
    }

    try {
      // Track the registration
      if (info && info.price > 0) {
        analytics.trackEventRegistration(
          info.event_id,
          info.name,
          "ticket",
          info.price,
          {
            organizationId: info.organization_id,
            organizationName: info.organization,
            registrationType: "paid",
            ticketType: "general",
          }
        );
      } else {
        analytics.trackEventRegistration(info.event_id, info.name, "free", 0, {
          organizationId: info.organization_id,
          organizationName: info.organization,
          registrationType: "free",
        });
      }

      // Existing implementation would go here
      console.log("Registering for event:", id);
    } catch (error) {
      console.error("Error registering for event:", error);

      // Track registration error
      analytics.trackError("registration_error", error.message, {
        eventId: info.event_id,
        eventName: info.name,
      });
    }
  }

  async function handleRegistrationRequest(id) {
    if (!user) {
      setButtonSignin(true);
      return;
    }

    if (!id) {
      return;
    }

    var currentEventData = await axios.get(`${api}events/${id}`, {
      headers: {
        "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
      },
    });

    if (!currentEventData?.data?.data) {
      console.error('Invalid API response structure');
      return;
    }

    currentEventData = currentEventData.data.data;
    const currRegisteredIndex = info.frequency === "Once" ? 0 : dateIndex;

    if (!currentEventData) {
      return;
    }

    if (currentEventData.registered?.[currRegisteredIndex]?.includes(email)) {
      return;
    }

    if (currentEventData.registered?.[currRegisteredIndex]) {
      currentEventData.registered[currRegisteredIndex].push(email);
    }
    currentEventData.pending_registrations.push({ email });

    try {
      await axios.put(`${api}events/${info.event_id}`, currentEventData, {
        withCredentials: true,
        headers: {
          "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
        },
      });

      //update user info
      const userDetails = await getUserDetails(email);
      userDetails.signedUpEvents.push(info.event_id);

      await updateUserDetails(email, {
        signedUpEvents: userDetails.signedUpEvents,
      });
    } catch (error) {
      console.error("Error updating event or user details:", error);
    }

    try {
      // Track the registration request
      analytics.trackInteraction("request", "registration", info.name, {
        eventId: info.event_id,
        organizationId: info.organization_id,
      });

      console.log("Requesting registration for event:", id);
      // Implementation would go here
    } catch (error) {
      console.error("Error requesting registration:", error);
      analytics.trackError("registration_request_error", error.message, {
        eventId: info.event_id,
        eventName: info.name,
      });
    }
  }

  // Add the missing handleUnRegister function
  async function handleUnRegister(id) {
    if (!user) {
      return;
    }

    if (!id) {
      return;
    }

    var currentEventData = await axios.get(`${api}events/${id}`, {
      headers: {
        "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
      },
    });
    currentEventData = currentEventData.data.data;
    const currRegisteredIndex = info.frequency === "Once" ? 0 : dateIndex;

    if (!currentEventData.registered?.[currRegisteredIndex]?.includes(email)) {
      return;
    }

    let index = currentEventData.registered?.[currRegisteredIndex]?.indexOf(email);

    if (index !== -1) {
      currentEventData.registered[currRegisteredIndex].splice(index, 1);
    }

    try {
      await axios.put(`${api}events/${info.event_id}`, currentEventData, {
        withCredentials: true,
        headers: {
          "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
        },
      });

      // Fetch the updated user details
      const userDetails = await getUserDetails(email);

      let index = userDetails.signedUpEvents.indexOf(info.event_id.toString());

      if (index !== -1) {
        await userDetails.signedUpEvents.splice(index, 1);
      }

      // Update the user's details in the database, with req.body which is signedUpEvents
      await updateUserDetails(email, {
        signedUpEvents: userDetails.signedUpEvents,
      });
    } catch (error) {
      console.error("Error updating event or user details:", error);
    }

    try {
      // Track unregistration
      analytics.trackInteraction("cancel", "registration", info.name, {
        eventId: info.event_id,
        organizationId: info.organization_id,
      });

      console.log("Unregistering from event:", id);
      // Implementation would go here
    } catch (error) {
      console.error("Error unregistering from event:", error);
      analytics.trackError("unregister_error", error.message, {
        eventId: info.event_id,
        eventName: info.name,
      });
    }
  }

  async function handleRegistrationApproval(email) {
    const userIndex = users.findIndex((user) => user.email === email);

    if (userIndex === -1) {
      console.error("user not found");
    }

    const userToApprove = users[userIndex];

    if (isStripe) {
      axios.post(
        `${api}stripe/approveRegistration`,
        {
          eventId: info.event_id,
          userEmail: userToApprove.email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        }
      );
    } else {
      //generate ticket
      let ticketURL;

      const ticketResponse = await axios.post(
        `${api}generateTicket`,
        {
          eventId: info.event_id,
          userId: userToApprove._id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        }
      );

      ticketURL = await uploadTicketImg(
        ticketResponse.data.data,
        info.event_id + "-" + email
      );

      // Create ticket in the new Tickets collection
      try {
        await axios.post(
          `${api}createFreeTicket`,
          {
            eventId: info.event_id,
            userId: userToApprove._id,
            email: userToApprove.email,
            qrCodeUrl: ticketURL,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
            },
          }
        );

        // Refresh the user's tickets for this event if they're the current user
        if (userInfo && userInfo.email === email) {
          await fetchUserTickets();
        }
      } catch (error) {
        console.error("Error creating ticket in new system:", error);
      }

      // For backward compatibility, still update the user's tickets object
      // This can be removed once the migration is complete
      let newTickets = userToApprove.tickets || {};
      newTickets[info.event_id] = ticketURL;

      await axios.put(
        `${api}users/${email}`,
        {
          tickets: newTickets,
        },
        {
          withCredentials: true,
          headers: {
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        }
      );

      //send confirmation email
      const payload = {
        email: email,
        eventName: info.name,
        eventURL: window.location.href,
        ticket: ticketURL, //ticketURL
        type: "Registration Request Approved",
        eventLocation: info.location,
        dateTime:
          new Date(startDate + "T00:00:00").toLocaleDateString("en-US") +
          " @ " +
          new Date(`2000-01-01T${startTime}`).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }),
      };

      try {
        await axios.post(`${api}sendEmail`, payload, {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
          },
        });
      } catch (error) {
        console.error(
          "Error:",
          error.response ? error.response.data : error.message
        );
      }
    }

    try {
      // Track approval
      analytics.trackInteraction("approve", "registration", email, {
        eventId: info.event_id,
        organizationId: info.organization_id,
      });

      console.log("Approving registration for:", email);
      // Implementation would go here
    } catch (error) {
      console.error("Error approving registration:", error);
      analytics.trackError("approval_error", error.message, {
        eventId: info.event_id,
        eventName: info.name,
        userEmail: email,
      });
    }
  }

  // Handle paid registration with tracking
  const handleStripeRedirect = async ({ tierPriceId, discountCode }) => {
    const selectedTier = info.pricingTiers.find(
      (tier) => tier.stripePriceId === tierPriceId
    );

    try {
      // Track before redirect
      if (info && info.event_id && selectedTier) {
        analytics.trackEventRegistration(
          info.event_id,
          info.name,
          selectedTier.name,
          selectedTier.price,
          {
            organizationId: info.organization_id,
            organizationName: info.organization,
            registrationType: "paid",
            ticketType: selectedTier.name,
            discountApplied: !!discountCode,
          }
        );
      }

      // Determine which endpoint to use based on whether we have a stripeAccountId
      if (hostOrg && hostOrg.stripeAccountId) {
        const { data } = await axios.post(
          `${api}stripe/create-checkout-session`,
          {
            eventId: info.event_id,
            organizationId: hostOrg.stripeAccountId,
            userEmail: loggedInEmail?.data || '',
            selectedPriceId: tierPriceId,
            discountCode: discountCode,
          }
        );

        // Redirect to Stripe Checkout
        window.location.href = data.sessionUrl;
      } else {
        // Existing functionality for regular stripe checkout
        const eventId = info.event_id;

        const response = await axios.post(
          `${api}stripe/checkoutSession`,
          {
            eventId,
            tierPriceId,
            discountCode,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
            },
          }
        );

        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error(
        "Error creating checkout session:",
        error.response?.data?.error || error.message
      );

      // Track payment error
      if (info && info.event_id) {
        analytics.trackError("payment_error", error.message, {
          eventId: info.event_id,
          eventName: info.name,
        });
      }
    }
  };

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  function formatEventTime() {
    // Combine date and time strings

    console.log(startDate);
    console.log(startTime);
    console.log(endDate);
    console.log(endTime);

    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);

    console.log(start);
    console.log(end);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error("Invalid date or time format");
      return {
        formattedDate: "Invalid date or time format",
        formattedTime: "",
      };
    }

    // Format the dates (e.g., "Mon, Jun 17" and "Thu, Jul 25")
    const dateFormatter = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });

    const startDateFormatted = dateFormatter.format(start);
    const endDateFormatted = dateFormatter.format(end);

    // Format the times (e.g., "8:30am" and "1:20pm")
    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    // Format start time and remove ':00' if present
    let startTimeFormatted = timeFormatter
      .format(start)
      .replace(/ /g, "")
      .replace("AM", " am")
      .replace("PM", "pm");
    if (
      startTimeFormatted.endsWith(":00am") ||
      startTimeFormatted.endsWith(":00pm")
    ) {
      startTimeFormatted = startTimeFormatted.replace(":00", "");
    }

    // Format end time and remove ':00' if present
    let endTimeFormatted = timeFormatter
      .format(end)
      .replace(/ /g, "")
      .replace("AM", " am")
      .replace("PM", "pm");
    if (
      endTimeFormatted.endsWith(":00am") ||
      endTimeFormatted.endsWith(":00pm")
    ) {
      endTimeFormatted = endTimeFormatted.replace(":00", "");
    }

    // Determine if the event spans multiple days
    let formattedDate;
    if (startDateFormatted === endDateFormatted) {
      // Same day
      formattedDate = `${startDateFormatted}`;
    } else {
      // Different days
      formattedDate = `${startDateFormatted} - ${endDateFormatted}`;
    }

    let formattedTime;
    if (startTimeFormatted === endTimeFormatted) {
      formattedTime = "Starts at " + startTimeFormatted;
    } else {
      formattedTime = `${startTimeFormatted} - ${endTimeFormatted}`;
    }

    return { formattedDate, formattedTime };
  }

  const { formattedDate, formattedTime } = formatEventTime();
  const formatPrice = (price) => {
    console.log("check if price is 0 or free", price);

    // Check if price is 0 or "Free" and return "Free" directly
    if (price === "0" || price === "Free") {
      return "Free";
    }

    // Convert price to string if it's a number
    let priceStr = price.toString();

    // Add '$' if it's not already present
    if (!priceStr.startsWith("$")) {
      priceStr = `$${priceStr}`;
    }

    return priceStr;
  };

  const getAttendeeDisplay = (info, dateIndex) => {
    // Get number of attendees based on approval requirement and date

    const dateIndex2 = registeredIndex;

    let attendeeCount;

    if (info.requireApproval) {
      // If approval required, count approved attendees
      if (
        isAdmin ||
        selectedOrganization === info.organization ||
        info.creator === loggedInEmail?.data
      ) {
        attendeeCount = info.registered?.[dateIndex2]?.length ?? 0;
      } else {
        attendeeCount = info.approvedAttendees?.[registeredIndex]?.length ?? 0;
      }
    } else {
      // Count registrations for specific date
      attendeeCount = info.registered?.[dateIndex2]?.length ?? 0;
    }

    // Display count with or without capacity
    if (info.capacity > 0) {
      return `${attendeeCount} / ${info.capacity}`;
    }

    return `${attendeeCount}`;
  };

  //checking if info tickets link has stripe
  // const isStripe = info.stripeProductId && info.stripePriceIds;
  const isStripe = info.stripeProductId && info.stripePriceIds?.length > 0;

  // Function to dynamically generate Open Graph meta tags
  const getMetaTags = () => {
    return (
      <Helmet>
        <title>{info.name}</title>
        <meta property="og:title" content={info.name} />
        <meta property="og:description" content={info.description} />
        <meta
          property="og:url"
          content={`https://www.ummahfy.com/event/${info.event_id}`}
        />
        <meta property="og:image" content={info.img} />
      </Helmet>
    );
  };

  // Function to check if event is full
  const isEventFull = () => {
    if (!info.capacity || info.capacity === 0) return false;

    const registeredCount = info.registered?.[registeredIndex]?.length ?? 0;

    return registeredCount >= info.capacity;
  };

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [customForm, setCustomForm] = useState(false);

  const handleCustomFormModal = () => {
    console.log("is bball working", customForm);
    setCustomForm(true);
  };

  const handleInputClick = () => {
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    console.log("MODAL CANCEL ");
    setIsModalVisible(false);
    setCustomForm(false);
    setScannerFromQR(false);
    setQrUserId(null);
  };

  const [form] = Form.useForm(); // Add this at the component level

  const [isTicketModalVisible, setIsTicketModalVisible] = useState(false);

  // Add these handler functions
  const showTicketModal = () => {
    setIsTicketModalVisible(true);
  };

  const handleTicketModalCancel = () => {
    setIsTicketModalVisible(false);
  };

  const [approvedAttendees, setApprovedAttendees] = useState(
    safeInfo.approvedAttendees?.[registeredIndex] || []
  );

  const onAttendeeApprovalChange = (approved) => {
    console.log(approved);
    setApprovedAttendees(approved);

    let newApprovedAttendees = safeInfo.approvedAttendees || [];
    newApprovedAttendees[registeredIndex] = approved;

    const response = axios.put(
      `${api}events/${info.event_id}`,
      {
        approvedAttendees: newApprovedAttendees,
      },
      {
        withCredentials: true,
        headers: {
          "x-api-key": process.env.REACT_APP_UMMAHFY_API_KEY,
        },
      }
    );
  };

  // Check if event exists before rendering
  if (!info) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '20px' }}>Event Not Found</h1>
        <p style={{ fontSize: '1.1rem', color: '#666' }}>The event you're looking for doesn't exist or has been removed.</p>
      </div>
    );
  }

  return (
    <>
      {getMetaTags()}

      {info.customFormQuestions && info.customFormQuestions.length > 0 && (
        <CustomFormModal
          isOpen={customForm}
          onClose={handleModalCancel}
          onSubmit={
            isStripe
              ? info.stripePriceIds.length > 1 || info.requireApproval
                ? () => setIsPricingModalOpen(true)
                : () =>
                  handleStripeRedirect({
                    tierPriceId: info.stripePriceIds[0].priceId,
                    discountCode: "",
                  })
              : info.requireApproval
                ? (values) => handleRegistrationRequest(id)
                : (values) => handleRegister(id)
          }
          form={form}
          info={info}
          isRegistered={isRegistered}
          loggedInEmail={loggedInEmail}
          isStripe={isStripe}
        />
      )}

      <div class="wrapper event-shell-wrapper">
        {console.log(loggedInUser?.data, "checking on event shell")}
        <Navbar />

        <div class="event-shell-container">
          <div class="flyer">
            <img
              className="imgStyle"
              src={info.img}
              loading="lazy"
              alt={`${info.name} event flyer`}
            />
          </div>

          <div class="title-register">
            <p class="event-shell-name"> {info.name} </p>

            {info.organization ? (
              <Link
                to={`/organization/${slugifyOrgName(info.organization)}`}
                className="avatar-org avatar-org-link"
                aria-label={`View ${info.organization}'s page`}
              >
                {hostOrg && hostOrg.profilePicture ? (
                  <img
                    className="avatar-icon"
                    src={hostOrg.profilePicture}
                    loading="lazy"
                    alt={`${info.organization} logo`}
                  />
                ) : (
                  <svg
                    className="avatar-icon"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path d="M12 2c5.52 0 10 4.48 10 10s-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2zM6.023 15.416C7.491 17.606 9.695 19 12.16 19c2.464 0 4.669-1.393 6.136-3.584A8.968 8.968 0 0 0 12.16 13a8.968 8.968 0 0 0-6.137 2.416zM12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                    </g>
                  </svg>
                )}
                <p className="event-shell-org">{info.organization}</p>
              </Link>
            ) : (
              <div className="avatar-org">
                <p className="event-shell-org">{info.organization}</p>
              </div>
            )}

            <>
              {user ? (
                <>
                  {isRegistered || isPaid ? (
                    <>
                      <div className="rsvp-ticket-btns">
                        {info.requireApproval ? (
                          // Handle approval required cases
                          <>
                            {isApproved ? (

                              <Button
                                className="already-registered-btn"
                                onClick={() => {
                                  setEditRsvp(true);
                                }}
                              >
                                Edit RSVP
                              </Button>

                            ) : (

                              <Button className="pending-approval-btn" disabled>
                                Registration Pending Approval
                              </Button>

                            )}
                          </>
                        ) : (
                          // No approval required - show regular edit RSVP button

                          <Button
                            className="already-registered-btn"
                            onClick={() => {
                              setEditRsvp(true);
                            }}
                          >
                            Edit RSVP
                          </Button>

                        )}


                        {isRegistered &&
                          (!info.requireApproval || isApproved) && (
                            <div>
                              {/* //user's ticket */}
                              <Button

                                className="ticket-btn"
                                onClick={showTicketModal}
                                icon={<TicketIcon />}
                              ></Button>
                            </div>
                          )}



                        {isRegistered &&
                          (!info.requireApproval || isApproved) && (
                            <div>
                              {/* add to apple wallet will go here */}
                              <AddToWalletButton
                                ticketData={{
                                  ticketId: info.event_id,
                                  eventName: info.name,
                                  location: info.location,
                                  datetime:
                                    info.dates[dateIndex].split("T")[0] +
                                    "T" +
                                    info.start_time,
                                  eventId: info.event_id,
                                  userId: userInfo._id,
                                }}
                              />
                            </div>
                          )}



                        {isRegistered &&
                          (!info.requireApproval || isApproved) && (
                            <div>
                              {/* add to calendar */}
                              <AddToCalendar
                                title={info.name}
                                description={info.descriptions}
                                dates={info.dates}
                                dateIndex={dateIndex} // Use first date
                                start_time={info.start_time}
                                end_time={info.end_time}
                                location={info.location}
                              />
                            </div>
                          )}


                        {/* Rest of the ticket modal code remains the same */}
                        <ConfigProvider
                          theme={{
                            components: {
                              Modal: {
                                titleFontSize: 20,
                              },
                            },
                          }}
                        >
                          <Modal
                            title="Your Ticket"
                            open={isTicketModalVisible}
                            onCancel={handleTicketModalCancel}
                            footer={null}
                            width={400}
                          >
                            {ticketsLoading ? (
                              <div
                                className="ticket-loading"
                                style={ticketLoadingStyle}
                              >
                                Loading tickets...
                              </div>
                            ) : userEventTickets &&
                              userEventTickets.length > 0 ? (
                              <div
                                className="tickets-container"
                                style={ticketsContainerStyle}
                              >
                                <div
                                  style={{
                                    textAlign: "center",
                                    marginBottom: "10px",
                                  }}
                                >
                                  Found {userEventTickets.length} ticket(s) for
                                  this event
                                </div>
                                {userEventTickets.map((ticket, index) => (
                                  <div
                                    key={ticket.ticketId}
                                    className="ticket-item"
                                    style={ticketItemStyle}
                                  >
                                    {/* Only show ticket number if there are multiple tickets */}
                                    {userEventTickets.length > 1 && (
                                      <h4 style={{ margin: "10px 0" }}>
                                        Ticket #{index + 1}
                                      </h4>
                                    )}
                                    <div
                                      className="ticket-content"
                                      style={ticketContentStyle}
                                    >
                                      <img
                                        style={ticketImageStyle}
                                        src={ticket.qrCodeUrl}
                                        alt={
                                          userEventTickets.length > 1
                                            ? `Ticket ${index + 1}`
                                            : "Ticket"
                                        }
                                      />
                                    </div>
                                    {ticket.isUsed && (
                                      <div style={ticketUsedStyle}>✓ Used</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div>
                                <div
                                  style={{
                                    textAlign: "center",
                                    marginBottom: "10px",
                                    color: "#666",
                                  }}
                                >
                                  No tickets found in the new system. Showing
                                  legacy ticket if available.
                                </div>
                                {/* Fallback to legacy ticket display */}
                                {userInfo.tickets &&
                                  userInfo.tickets[info.event_id] ? (
                                  <div
                                    className="ticket-content"
                                    style={ticketContentStyle}
                                  >
                                    <img
                                      style={ticketImageStyle}
                                      src={userInfo.tickets[info.event_id]}
                                      alt="Ticket"
                                    />
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                      color: "#666",
                                    }}
                                  >
                                    No tickets found for this event.
                                  </div>
                                )}
                              </div>
                            )}
                          </Modal>
                        </ConfigProvider>
                      </div>

                      <EditRSVP
                        id={id}
                        handleUnRegister={handleUnRegister}
                        trigger={editRsvp}
                        setTrigger={setEditRsvp}
                        isPaid={isPaid}
                        info={info}
                        isRegistered={isRegistered}
                        isStripe={isStripe}
                      />
                    </>
                  ) : (
                    <>
                      <>
                        {(info.requireApproval &&
                          info.capacity > 0 &&
                          (info.approvedAttendees?.[registeredIndex]?.length ?? 0) >=
                          info.capacity) ||
                          (!info.requireApproval &&
                            info.capacity > 0 &&
                            (info.registered?.[registeredIndex]?.length ?? 0) >=
                            info.capacity) ? (
                          <ConfigProvider
                            theme={{
                              components: {
                                Button: {
                                  defaultBg: 'rgba(255, 255, 255, 0.04)',
                                  controlHeight: 36,
                                  borderRadius: 4,
                                  defaultBorderColor: 'rgba(255, 255, 255, 0.04)',
                                  colorText: '#737373',
                                  defaultHoverBg: 'rgba(255, 255, 255, 0.04)',
                                  defaultHoverBorderColor: 'rgba(255, 255, 255, 0.04)',
                                  defaultHoverColor: '#737373',
                                },
                              },
                            }}
                          >
                            <Button
                              className="passed-button"
                              disabled
                              title="Event has reached capacity"
                            >
                              Sold Out
                            </Button>
                          </ConfigProvider>
                        ) : (
                          <>
                            {info.customFormQuestions &&
                              info.customFormQuestions.length > 0 ? (

                              <Button
                                className="register-btn"
                                onClick={handleCustomFormModal}
                              >
                                {info.requireApproval
                                  ? "Request Registration"
                                  : isStripe
                                    ? "Purchase Tickets"
                                    : "Register for this Event"}
                              </Button>

                            ) : (
                              <>
                                {isStripe ? (

                                  <Button
                                    className="register-btn"
                                    onClick={() => {
                                      if (
                                        info.stripePriceIds.length > 1 ||
                                        info.requireApproval
                                      ) {
                                        setIsPricingModalOpen(true);
                                      } else {
                                        handleStripeRedirect({
                                          tierPriceId:
                                            info.stripePriceIds[0].priceId,
                                          discountCode: "",
                                        });
                                      }
                                    }}
                                  >
                                    Purchase Tickets
                                  </Button>
                                ) : (

                                  <Button
                                    className="register-btn"
                                    onClick={() => {
                                      info.requireApproval
                                        ? handleRegistrationRequest(id)
                                        : handleRegister(id);
                                      info.requireApproval
                                        ? setRequestRegistrationModal(true)
                                        : setRegisterModal(true);
                                    }}
                                  >
                                    {info.requireApproval
                                      ? "Request Registration"
                                      : "Register for this Event"}
                                  </Button>

                                )}
                              </>
                            )}
                          </>
                        )}
                      </>

                      {isStripe && (
                        <PricingTierModal
                          isOpen={isPricingModalOpen}
                          onClose={() => setIsPricingModalOpen(false)}
                          onSubmit={handleStripeRedirect}
                          handleRegistrationRequest={handleRegistrationRequest}
                          pricingTiers={
                            info.stripePriceIds || [
                              {
                                priceId: info.stripePriceIds[0],
                                name: "Standard Ticket",
                                price: info.price,
                              },
                            ]
                          }
                          info={info}
                        />
                      )}
                      {!isStripe &&
                        !(
                          info.customFormQuestions &&
                          info.customFormQuestions.length > 0
                        ) && (
                          <RegisterModal
                            trigger={registerModal}
                            setTrigger={setRegisterModal}
                            info={info}
                          />
                        )}
                      {!isStripe &&
                        !(
                          info.customFormQuestions &&
                          info.customFormQuestions.length > 0
                        ) && (
                          <RequestRegistrationModal
                            trigger={requestRegistrationModal}
                            setTrigger={setRequestRegistrationModal}
                            info={info}
                          />
                        )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {(info.requireApproval &&
                    info.capacity > 0 &&
                    (info.approvedAttendees?.[registeredIndex]?.length ?? 0) >=
                    info.capacity) ||
                    (!info.requireApproval &&
                      info.capacity > 0 &&
                      (info.registered?.[registeredIndex]?.length ?? 0) >=
                      info.capacity) ? (
                    <ConfigProvider
                      theme={{
                        components: {
                          Button: {
                            defaultBg: 'rgba(255, 255, 255, 0.04)',
                            controlHeight: 36,
                            borderRadius: 4,
                            defaultBorderColor: 'rgba(255, 255, 255, 0.04)',
                            colorText: '#737373',
                            defaultHoverBg: 'rgba(255, 255, 255, 0.04)',
                            defaultHoverBorderColor: 'rgba(255, 255, 255, 0.04)',
                            defaultHoverColor: '#737373',
                          },
                        },
                      }}
                    >
                      <Button
                        className="passed-button"
                        disabled
                        title="Event has reached capacity"
                      >
                        Sold Out
                      </Button>
                    </ConfigProvider>
                  ) : (

                    <Button
                      className="register-btn"
                      onClick={() => {
                        setButtonSignin(true);
                      }}
                    >
                      Register for this Event
                    </Button>

                  )}
                  <Signin trigger={buttonSignin} setTrigger={setButtonSignin} />
                  <OrganizerSignIn
                    trigger={organizerSignin}
                    setTrigger={setOrganizerSignin}
                  />
                </>
              )}
            </>
          </div>

          <div class="event-shell-info">
            <div class="date-time-loc-div">
              <div class="event-shell-date">
                <CalendarOutlined className="calendar-icon" />
                <p class="event-shell-datetime">{formattedDate}</p>
              </div>

              <div class="event-shell-time">
                <ClockCircleOutlined className="clock-icon" />
                <p>{formattedTime}</p>
              </div>

              <div className="event-shell-location">
                {console.log(info, "this is the virtual link")}

                {info.hideLocation &&
                  (!isRegistered || info.requireApproval || !isApproved) ? (
                  <div className="es-location-link">
                    <MapPin className="location-icon" />
                    <p>Register to view location</p>
                  </div>
                ) : info.location && info.location !== "" ? (
                  <a
                    className="es-location-link"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      info.location
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPin className="location-icon" />
                    <p>{info.location}</p>
                  </a>
                ) : info.virtual_link && info.virtual_link !== "" ? (
                  <a
                    className="es-location-link"
                    href={info.virtual_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LaptopOutlined className="location-icon" />
                    <p>{info.virtual_link}</p>
                  </a>
                ) : (
                  <div className="no-location">
                    <Location className="location-icon" />
                    <p>No location given, check organizer's site.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="event-shell-description">
              <div dangerouslySetInnerHTML={{ __html: info.description }} />
            </div>

            <div className="badge-grp bdgrp-evsh">
              {info.frequency && info.frequency !== "Once" && (
                <div className="badge time-badge">
                  <p>{capitalizeFirstLetter(info.frequency)}</p>
                </div>
              )}

              {info.age_range && info.age_range !== "all" && (
                <div className="badge age-badge">
                  <p>{capitalizeFirstLetter(info.age_range)}</p>
                </div>
              )}

              {info.gender && info.gender !== ("all" || "") && (
                <div className="badge gender-badge">
                  <p>{capitalizeFirstLetter(info.gender)}</p>
                </div>
              )}

              <div className="badge price-badge">
                <p>{formatPrice(info.price)}</p>
              </div>

              {info.tags.map((tag, index) => (
                <div key={index} className="badge tag-badge">
                  <p>{tag}</p>
                </div>
              ))}
            </div>
          </div>
          <div class="attendees-share">
            {(isAdmin ||
              selectedOrganization === info.organization ||
              info.creator === loggedInEmail?.data) && (
                <AttendeesModal
                  isVisible={isModalVisible}
                  onCancel={handleModalCancel}
                  info={info}
                  dateIndex={dateIndex}
                  getAttendeeDisplay={getAttendeeDisplay}
                  approvedAttendees={approvedAttendees}
                  onApprovalChange={onAttendeeApprovalChange}
                  registeredIndex={registeredIndex}
                  handleRegistrationApproval={handleRegistrationApproval}
                  setApprovedAttendees={setApprovedAttendees}
                  isStripe={isStripe}
                />
              )}


            <Tooltip title="Only organizers can see attendees">
              <Button
                className="capacity-attendees"
                onClick={handleInputClick}
              >
                {!("hideCapacity" in info) ||
                  info.hideCapacity === false ||
                  isAdmin ||
                  selectedOrganization === info.organization ||
                  info.creator === loggedInEmail?.data ? (
                  <div className="attendees-number-div">
                    <Users className="attendees-icon" />
                    {getAttendeeDisplay(info, registeredIndex)}
                  </div>
                ) : info.hideCapacity ? (
                  <div className="attendees-number-div">
                    <Users className="attendees-icon" />
                    Hidden
                  </div>
                ) : null}
              </Button>
            </Tooltip>


            {user && (isAdmin || selectedOrganization === info.organization) ? (
              <>
                <TicketScanner
                  info={info}
                  registeredIndex={registeredIndex}
                  fromQR={scannerFromQR}
                  qrUserId={qrUserId}
                  isOpen={isTickerScannerOpen}
                  onOpen={handleOpenTicketScanner}
                  onClose={handleCloseTicketScanner}
                />
              </>
            ) : (
              <ShareButton
                buttontext="Copy Link"
                styling="es-share-button"
                iconstyling="share-button-icon"
              />
            )}
          </div>
          <div class="rect5">
            <GoogleMap
              address={
                info.hideLocation && !isRegistered
                  ? info.location.substring(info.location.indexOf(",") + 2)
                  : info.location
              }
            />
          </div>

          <div class="eventshell-edit-event">
            {user ? (
              <>
                {isAdmin || selectedOrganization === info.organization ? (
                  <>
                    <Button
                      className="already-registered-btn"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setOrgEditEvent(true);
                      }}
                    >
                      Edit Event Details
                    </Button>

                    <OrgEditEvent
                      info={info}
                      trigger={orgEditEvent}
                      setTrigger={setOrgEditEvent}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>

      <Footer> </Footer>
    </>
  );
};

export default EventShell;
